import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";

export const Route = createFileRoute("/commercial-roofing")({
  head: () => ({
    meta: [
      { title: "Commercial Roofing in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Commercial roof repair, replacement, metal and low-slope roofing services in Citrus County. Request a property-specific inspection and estimate." },
      { property: "og:title", content: "Commercial Roofing in Citrus County, FL" },
      { property: "og:description", content: "Commercial roof inspections, repair, replacement, metal and low-slope systems." },
      { property: "og:url", content: canonical("/commercial-roofing") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/commercial-roofing") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Commercial Roofing", href: "/commercial-roofing" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Commercial Roofing",
          serviceType: "Commercial Roofing",
          description: "Commercial roof repair, replacement, metal and low-slope roofing in Citrus County, FL.",
          url: canonical("/commercial-roofing"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Commercial Roofing", href: "/commercial-roofing" }]} />
      <PageHero showRating
        eyebrow="Commercial Roofing"
        title="Commercial Roofing in Citrus County, FL"
        subtitle="Property-specific inspection, repair and replacement for small commercial buildings across Citrus County. Planned around your operations."
      />
      <ServicePageBody title="Commercial Roofing" service="Commercial Roofing">
        <h2>Commercial roof inspections</h2>
        <p>
          A commercial roof inspection looks at membrane condition, seams, flashings around HVAC and other rooftop equipment, penetrations, drainage and any evidence of ponding or previous leak repair. We provide a written estimate for any recommended work.
        </p>

        <h2>Commercial roof repair</h2>
        <p>
          Most commercial roof leaks trace back to failed seams, flashings around penetrations, or damage around rooftop equipment. We repair those failure points instead of just applying sealant on top of the problem.
        </p>

        <h2>Commercial roof replacement</h2>
        <p>
          When a commercial roof reaches the end of its life, we help you plan the replacement — including material selection, staging around building operations and coordination with any rooftop equipment work.
        </p>

        <h2>Metal and low-slope systems</h2>
        <p>
          We install metal roof systems and low-slope membrane systems appropriate to the building. See <Link to="/metal-roofing">metal roofing</Link> and <Link to="/flat-roofing">flat roofing</Link> for the residential and light-commercial systems we install.
        </p>

        <h2>Minimizing disruption</h2>
        <p>
          For occupied commercial buildings, we plan work around your hours where possible and communicate clearly about staging, dumpsters and access.
        </p>

        <h2>Property-specific planning</h2>
        <p>
          Every commercial roof is different. Request a walk-through and we'll build a specific plan for your building rather than pushing a one-size-fits-all package.
        </p>
      </ServicePageBody>
    </>
  );
}
